import PropTypes from 'prop-types';
import React from 'react';
import { M } from '@dashboard-experience/mastodon';
import { i18n } from '@international/mastodon-i18n';

import T from 'components/T';
import Panel from '../../../Panels';
import Attribute from '../Attribute';
import { dateIfDate } from '../lib/formatting';
import { hasAssessment, getScreeningStatusAndAssessment } from '../utils';
import {
  MVR_VIOLATION_FIELDS,
  MVR_ACCIDENT_FIELDS,
  MVR_SUSPENSION_FIELDS,
  MVR_MISCELLANEOUS_FIELDS,
} from '../../../../constants';
import MedicalCertificates from './MedicalCertificates';
import { getRandomKey } from '../../../../lib/helpers';

const CovidNotice = () => (
  <div className='cp-card covid-19-notice'>
    <div className='cp-card-body'>
      <p>
        <strong>
          <M.Icon icon='WarningAltFilled' />
          <T value='components.Report.MotorVehicleReport.covidExtensionNotice.text' />
        </strong>
      </p>
    </div>
  </div>
);

class MotorVehicleReport extends React.Component {
  nameMatches(licenseReport) {
    return (
      licenseReport.nameMatches === undefined ||
      licenseReport.nameMatches === true
    );
  }

  dobMatches(licenseReport) {
    return (
      licenseReport.dobMatches === undefined ||
      licenseReport.dobMatches === true
    );
  }

  showLicenseReportData(licenseReport) {
    return this.nameMatches(licenseReport) && this.dobMatches(licenseReport);
  }

  licenseResultStatus(licenseReport) {
    if (!this.nameMatches(licenseReport) && !this.dobMatches(licenseReport)) {
      return i18n.getStr(
        'components.Report.MotorVehicleReport.errors.nameAndDobMismatch',
      );
    }
    if (!this.nameMatches(licenseReport)) {
      return i18n.getStr(
        'components.Report.MotorVehicleReport.errors.nameMismatch',
      );
    }
    if (!this.dobMatches(licenseReport)) {
      return i18n.getStr(
        'components.Report.MotorVehicleReport.errors.dobMismatch',
      );
    }

    return undefined;
  }

  renderIncidentAttributes(attrs, fields) {
    return fields.map(key => {
      let value = attrs[key];

      if (key === 'dispositionDate') {
        // convictionDate value on violations should be displayed as "Disposition Date"
        value = value || attrs.convictionDate;
      }

      return <Attribute key={getRandomKey()} attr={key} value={value} />;
    });
  }

  renderCdlisRecord(cdlisRecord) {
    return (
      <Panel
        title={i18n.getStr('components.Report.MotorVehicleReport.cdlisResults')}
        className='cdlis-record card-plain card-border'
        key='cdlis-records-panel'
      >
        {cdlisRecord.driverLicenses.map((driverLicense, i) => (
          <div
            className='cdlis-attributes card-plain'
            key={`cdlis-license-panel-${i + 1}`}
          >
            <div
              className='cdlis-attributes mt-2 mb-5'
              key={`cdlis-attributes-${i + 1}`}
            >
              <Attribute
                attr={i18n.getStr(
                  'components.Report.MotorVehicleReport.driverName',
                )}
                value={`${driverLicense.driver.name.firstName}
                  ${driverLicense.driver.name.middleName}
                  ${driverLicense.driver.name.lastName}
                  ${driverLicense.driver.name.suffix}`}
              />
              <Attribute
                attr={i18n.getStr('components.Report.MotorVehicleReport.dob')}
                value={dateIfDate(driverLicense.driver.dob) || '-'}
              />
              <Attribute
                attr={i18n.getStr(
                  'components.Report.MotorVehicleReport.licenseNumber',
                )}
                value={driverLicense.number.toString()}
              />
              <Attribute
                attr={i18n.getStr(
                  'components.Report.MotorVehicleReport.licenseState',
                )}
                value={driverLicense.state}
              />
              <Attribute
                attr={i18n.getStr(
                  'components.Report.MotorVehicleReport.currentLicense',
                )}
                value={driverLicense.current.toString()}
              />
            </div>
          </div>
        ))}
      </Panel>
    );
  }

  renderLicenseReports(licenseReports, isAssessmentEnabled) {
    return licenseReports.map((licenseReport, i) => (
      <React.Fragment key={`license-report-${i + 1}`}>
        {this.showLicenseReportData(licenseReport) && (
          <div>
            <div
              className='license-attributes mb-5'
              key={`license-attributes-${i + 1}`}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {licenseReport?.assessment?.display?.decision &&
                  isAssessmentEnabled && (
                    <M.StatusIcon
                      className='mr-2'
                      icon={licenseReport.assessment.display.decision}
                      data-testid='attribute-status-icon'
                    />
                  )}
                <h6>{`${licenseReport.number} (${licenseReport.state})`}</h6>
              </div>
              <hr className='mb-2 mt-2' />
              {licenseReport.covidExtension && <CovidNotice />}
              <Attribute
                attr={i18n.getStr(
                  'components.Report.MotorVehicleReport.licenseStatus',
                )}
                value={licenseReport.status || '-'}
              />
              <Attribute
                attr={i18n.getStr(
                  'components.Report.MotorVehicleReport.licenseType',
                )}
                value={licenseReport.type || '-'}
              />
              <Attribute
                attr={i18n.getStr(
                  'components.Report.MotorVehicleReport.licenseClass',
                )}
                value={licenseReport.class || '-'}
              />
              <Attribute
                attr={i18n.getStr(
                  'components.Report.MotorVehicleReport.licenseExpirationDate',
                )}
                value={dateIfDate(licenseReport.expirationDate) || '-'}
              />
              {licenseReport.expirationExtensionRecord && (
                <Attribute
                  attr={i18n.getStr(
                    'components.Report.MotorVehicleReport.licenseExtendedExpirationDate',
                  )}
                  value={
                    dateIfDate(
                      licenseReport.expirationExtensionRecord
                        .extendedExpirationDate,
                    ) || '-'
                  }
                />
              )}
              <Attribute
                attr={i18n.getStr(
                  'components.Report.MotorVehicleReport.licenseIssuedDate',
                )}
                value={dateIfDate(licenseReport.issuedDate) || '-'}
              />
              <Attribute
                attr={i18n.getStr(
                  'components.Report.MotorVehicleReport.licenseFirstIssuedDate',
                )}
                value={dateIfDate(licenseReport.firstIssuedDate) || '-'}
              />
              <Attribute
                attr={i18n.getStr(
                  'components.Report.MotorVehicleReport.inferredIssuedDate',
                )}
                value={dateIfDate(licenseReport.inferredIssuedDate) || '-'}
              />
              <Attribute
                attr={i18n.getStr(
                  'components.Report.MotorVehicleReport.licenseDobReturned',
                )}
                value={dateIfDate(licenseReport.dob) || '-'}
              />
              <M.Grid>
                <M.GridRow className='mb-1'>
                  <M.GridCol sm={4} md={3} className='text-very-muted'>
                    <T value='components.Report.MotorVehicleReport.note' />
                  </M.GridCol>
                  <M.GridCol
                    className='text-very-muted font-italic'
                    style={{ lineHeight: '1rem' }}
                  >
                    {i18n.getStr(
                      'components.Report.MotorVehicleReport.licenseDisclaimerNote',
                    )}
                  </M.GridCol>
                </M.GridRow>
              </M.Grid>
            </div>

            {licenseReport.medicalCertificates?.length && (
              <MedicalCertificates
                medicalCertificates={licenseReport.medicalCertificates}
              />
            )}
            {this.renderRestrictions(licenseReport.restrictions)}
            {this.renderEndorsements(licenseReport.endorsements)}
            {this.renderViolations(
              licenseReport.violations,
              isAssessmentEnabled,
            )}
            {this.renderAccidents(licenseReport.accidents, isAssessmentEnabled)}
            {this.renderSuspensions(
              licenseReport.suspensions,
              isAssessmentEnabled,
            )}
            {this.renderMiscellaneousIncidents(
              licenseReport.miscellaneousIncidents,
            )}
          </div>
        )}
      </React.Fragment>
    ));
  }

  renderViolations(violations, isAssessmentEnabled) {
    if (!violations.length) {
      return null;
    }

    return (
      <div className='mb-5'>
        <h6>
          <T value='components.Report.MotorVehicleReport.violations' />
        </h6>
        {violations.map((violation, i) => (
          <div className='mvr-violation mb-4' key={`violation-${i + 1}`}>
            <hr className='mb-2 mt-2' />
            <div className='d-flex align-items-center'>
              {isAssessmentEnabled && violation.assessment.display.decision && (
                <M.StatusIcon
                  icon={violation.assessment.display.decision}
                  className='mr-2'
                />
              )}
              <span className='text-uppercase'>{violation.description}</span>
              <span className='ml-auto'>
                {dateIfDate(violation.issuedDate)}
              </span>
            </div>
            <hr className='mb-2 mt-2' />
            <div className='mb-4 mt-4'>
              {this.renderIncidentAttributes(violation, MVR_VIOLATION_FIELDS)}
            </div>
          </div>
        ))}
      </div>
    );
  }

  renderSuspensions(suspensions, isAssessmentEnabled) {
    if (!(suspensions && suspensions.length)) {
      return null;
    }

    return (
      <div className='mb-5'>
        <h6>
          {i18n.getStr('components.Report.MotorVehicleReport.suspensions')}
        </h6>
        {suspensions.map((suspension, i) => (
          <div className='mvr-suspension mb-4' key={`suspension-${i + 1}`}>
            <hr className='mb-2 mt-2' />
            <div className='d-flex align-items-center'>
              {isAssessmentEnabled &&
                suspension.assessment?.display?.decision && (
                  <M.StatusIcon
                    icon={suspension.assessment.display.decision}
                    className='mr-2'
                  />
                )}
              <span className='text-uppercase'>{suspension.description}</span>
              <span className='ml-auto'>
                {dateIfDate(suspension.startDate)}
              </span>
            </div>
            <hr className='mb-2 mt-2' />
            <div className='mb-4 mt-4'>
              {this.renderIncidentAttributes(suspension, MVR_SUSPENSION_FIELDS)}
            </div>
          </div>
        ))}
      </div>
    );
  }

  renderMiscellaneousIncidents(miscellaneous_incidents) {
    if (!(miscellaneous_incidents && miscellaneous_incidents.length)) {
      return null;
    }

    return (
      <div className='mb-5'>
        <h6>
          {i18n.getStr(
            'components.Report.MotorVehicleReport.miscellaneousIncidents',
          )}
        </h6>
        {miscellaneous_incidents.map((incident, i) => (
          <div
            className='mvr-miscellaneous-incident mb-4'
            key={`miscellaneous-${i + 1}`}
          >
            <hr className='mb-2 mt-2' />
            <div className='d-flex'>
              <span className='text-uppercase'>{incident.description}</span>
              <span className='ml-auto'>{dateIfDate(incident.date)}</span>
            </div>
            <hr className='mb-2 mt-2' />
            <div className='mb-4 mt-4'>
              {this.renderIncidentAttributes(
                incident,
                MVR_MISCELLANEOUS_FIELDS,
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  renderAccidents(accidents, isAssessmentEnabled) {
    if (!accidents.length) {
      return null;
    }

    return (
      <div className='mb-5'>
        <h6>{i18n.getStr('components.Report.MotorVehicleReport.accidents')}</h6>
        {accidents.map((accident, i) => (
          <div className='mvr-accident mb-4' key={`accident-${i + 1}`}>
            <hr className='mb-2 mt-2' />
            <div className='d-flex align-items-center'>
              {isAssessmentEnabled &&
                accident.assessment?.display?.decision && (
                  <M.StatusIcon
                    icon={accident.assessment.display.decision}
                    className='mr-2'
                  />
                )}
              <span className='text-uppercase'>{accident.description}</span>
              <span className='ml-auto'>
                {dateIfDate(accident.accidentDate)}
              </span>
            </div>
            <hr className='mb-2 mt-2' />
            <div className='mb-4 mt-4'>
              {this.renderIncidentAttributes(accident, MVR_ACCIDENT_FIELDS)}
              <M.Grid>
                <M.GridRow className='mb-1'>
                  <M.GridCol sm={4} md={3} className='text-very-muted'>
                    <T value='components.Report.MotorVehicleReport.note' />
                  </M.GridCol>
                  <M.GridCol
                    sm={8}
                    md={9}
                    className='text-very-muted font-italic'
                  >
                    {accident.note}
                  </M.GridCol>
                </M.GridRow>
              </M.Grid>
            </div>
          </div>
        ))}
      </div>
    );
  }

  renderRestrictions(restrictions) {
    if (!restrictions.length) {
      return null;
    }

    return (
      <div className='mb-5'>
        <h6>
          <T value='components.Report.MotorVehicleReport.restrictions' />
        </h6>
        <hr className='mb-2 mt-2' />
        <ul className='list-unstyled'>
          {restrictions.map((restriction, i) => (
            <li
              className='mvr-restriction text-uppercase'
              key={`restriction-${i + 1}`}
            >
              {restriction}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  renderEndorsements(endorsements) {
    if (!endorsements.length) {
      return null;
    }

    return (
      <div className='mb-5'>
        <h6>
          {i18n.getStr('components.Report.MotorVehicleReport.endorsements')}
        </h6>
        <hr className='mb-2 mt-2' />
        <ul className='list-unstyled'>
          {endorsements.map((endorsement, i) => (
            <li
              className='mvr-endorsement text-uppercase'
              key={`endorsement-${i + 1}`}
            >
              {endorsement.dmvDescription}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  render() {
    const { canada, search } = this.props;
    const isAssessmentEnabled = hasAssessment(search);
    if (!search || search.status === 'canceled') {
      return null;
    }

    const assessEnabled = true;
    const useScreeningStatusOverAssessment = false;
    const { searchStatus, statusType, secondaryStatus } =
      getScreeningStatusAndAssessment(
        assessEnabled,
        useScreeningStatusOverAssessment,
        search,
      );

    return (
      <Panel
        title={i18n.getStr('components.Report.MotorVehicleReport.title')}
        status={searchStatus}
        statusType={statusType}
        secondaryStatus={secondaryStatus}
      >
        {(search.notFound ||
          search.experienceFailed ||
          search.unsupportedProvince) && (
          <div className='mb-3'>
            {search.notFound && search.cdlisRecord === null && (
              <span className='cdlis-record-not-found'>
                {i18n.getStr(
                  'components.Report.MotorVehicleReport.cdlisRecordsNotFound',
                )}
              </span>
            )}
            {search.unsupportedProvince && (
              <span
                key='idh'
                className='cp-badge cp-badge-warning text-capitalize'
              >
                <T
                  value='components.Report.MotorVehicleReport.unsupportedProvince'
                  province={search.province}
                />
              </span>
            )}
          </div>
        )}

        {/* If cdlis_record is defined, assume DotMvr resource type */}
        {!canada &&
          !!search.cdlisRecord &&
          this.renderCdlisRecord(search.cdlisRecord)}

        {/* If licenseReports is defined, assume MultiMvr resource type */}
        {!canada &&
          !!search.licenseReports &&
          this.renderLicenseReports(search.licenseReports, isAssessmentEnabled)}

        {!canada && !search.licenseReports && (
          <div className='mb-5'>
            <Attribute
              attr={i18n.getStr(
                'components.Report.MotorVehicleReport.fullName',
              )}
              value={search.fullName || '-'}
            />
            <Attribute
              attr={i18n.getStr(
                'components.Report.MotorVehicleReport.currentLicenseStatus',
              )}
              value={search.licenseStatus || '-'}
            />
            <Attribute
              attr={i18n.getStr(
                'components.Report.MotorVehicleReport.currentLicenseType',
              )}
              value={search.licenseType || '-'}
            />
            <Attribute
              attr={i18n.getStr(
                'components.Report.MotorVehicleReport.currentLicenseClass',
              )}
              value={search.licenseClass || '-'}
            />
            <Attribute
              attr={i18n.getStr(
                'components.Report.MotorVehicleReport.currentLicenseExpirationDate',
              )}
              value={dateIfDate(search.expirationDate) || '-'}
            />
            <Attribute
              attr={i18n.getStr(
                'components.Report.MotorVehicleReport.currentLicenseIssuedDate',
              )}
              value={dateIfDate(search.issuedDate) || '-'}
            />
            <Attribute
              attr={i18n.getStr(
                'components.Report.MotorVehicleReport.currentLicenseFirstIssuedDate',
              )}
              value={dateIfDate(search.firstIssuedDate) || '-'}
            />
          </div>
        )}

        {canada && (
          <div className='mb-5'>
            <Attribute
              attr={i18n.getStr(
                'components.Report.MotorVehicleReport.fullName',
              )}
              value={search.fullName || '-'}
            />
            <Attribute
              attr={i18n.getStr(
                'components.Report.MotorVehicleReport.currentDriverStatus',
              )}
              value={search.driverStatus || '-'}
            />
            <Attribute
              attr={i18n.getStr(
                'components.Report.MotorVehicleReport.currentDriverClass',
              )}
              value={search.driverClass || '-'}
            />
            <Attribute
              attr={i18n.getStr(
                'components.Report.MotorVehicleReport.currentLicenseNumber',
              )}
              value={search.licenseNumber || '-'}
            />
            <Attribute
              attr={i18n.getStr(
                'components.Report.MotorVehicleReport.currentLicenseProvince',
              )}
              value={search.province || '-'}
            />
            <Attribute
              attr={i18n.getStr(
                'components.Report.MotorVehicleReport.currentLicenseType',
              )}
              value={search.licenseType || '-'}
            />
            <Attribute
              attr={i18n.getStr(
                'components.Report.MotorVehicleReport.currentLicenseExpiryDate',
              )}
              value={dateIfDate(search.expiryDate) || '-'}
            />
            <Attribute
              attr={i18n.getStr(
                'components.Report.MotorVehicleReport.currentLicenseOriginalDate',
              )}
              value={dateIfDate(search.originalDate) || '-'}
            />
            <Attribute
              attr={i18n.getStr(
                'components.Report.MotorVehicleReport.currentLicensePoints',
              )}
              value={search.points || '-'}
            />
          </div>
        )}

        {(canada || !search.licenseReports) &&
          this.renderEndorsements(search.endorsements)}
        {(canada || !search.licenseReports) &&
          this.renderViolations(search.violations, isAssessmentEnabled)}
        {(canada || !search.licenseReports) &&
          this.renderSuspensions(search.suspensions, isAssessmentEnabled)}
        {(canada || !search.licenseReports) &&
          this.renderAccidents(search.accidents, isAssessmentEnabled)}
        {(canada || !search.licenseReports) &&
          this.renderRestrictions(search.restrictions)}
        {(canada || !search.licenseReports) &&
          this.renderMiscellaneousIncidents(search.miscellaneousIncidents)}
      </Panel>
    );
  }
}

MotorVehicleReport.propTypes = {
  canada: PropTypes.bool,
  search: PropTypes.object,
};

MotorVehicleReport.defaultProps = {
  search: null,
  canada: false,
};

export default MotorVehicleReport;
